"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Shield, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const trustPills = [{
  id: 1,
  label: "Real-time threat protection"
}, {
  id: 2,
  label: "Multi-device coverage"
}];
const features = [{
  id: 1,
  text: "Real-time virus and malware protection"
}, {
  id: 2,
  text: "Banking and browsing protection"
}, {
  id: 3,
  text: "Family and parental controls"
}];
export default function FsecurePromo() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="fsecurepromo" data-nav="dark" className="relative overflow-hidden bg-[hsl(221,40%,4%)] text-white py-20 lg:py-32">
      {/* Decorative faded background watermark */}
      <span aria-hidden="true" className="pointer-events-none select-none absolute -top-12 -left-6 font-mono text-[200px] md:text-[280px] font-bold leading-none text-white/5">
        04
      </span>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Brand & Overview */}
          <div className="lg:col-span-5">
            <motion.div variants={itemVariants} className="flex items-center gap-3">
              <span className="h-6 w-1 rounded-full bg-primary" />
              <span className="font-mono text-4xl font-semibold tracking-widest text-primary uppercase sm:text-5xl lg:text-6xl">
                F-Secure
              </span>
            </motion.div>

            <motion.h2 variants={itemVariants} className="mt-4 font-default text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Complete protection against online threats
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-6 text-base leading-relaxed text-white/70 lg:text-lg">
              Safeguard your personal computers, phones, and tablets with automated defenses.
              Block malicious files, sneaky ransomware, and fake payment sites without slowing
              down your day.
            </motion.p>

            {/* Trust Indicator Pills */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap gap-3">
              {trustPills.map((pill, i) => <div key={pill.id} data-index={i} className="flex items-center gap-2 rounded-md border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] px-3.5 py-2 text-sm text-white/85 shadow-sm">
                  <ShieldCheck className="size-4 shrink-0 text-primary" />
                  <span>{pill.label}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column: Featured Promotion Card */}
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <div className="relative">
              {/* L-shaped corner accent brackets */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-2.5 -left-2.5 size-7 border-t-2 border-l-2 border-primary" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-2.5 -right-2.5 size-7 border-b-2 border-r-2 border-primary" />

              <Card className="rounded-lg border border-[hsl(221,30%,15%)] bg-[hsl(221,30%,8%)] p-6 md:p-12 shadow-2xl text-white">
                <CardContent className="p-0">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex size-14 items-center justify-center rounded-lg bg-primary/10 text-primary md:size-16">
                      <Shield className="size-8 md:size-9" />
                    </div>
                    <Badge variant="outline" className="border-primary/20 bg-primary/10 px-3 py-1 font-mono text-xs uppercase tracking-wider text-primary">
                      Best for all-in-one security
                    </Badge>
                  </div>

                  <h3 className="mt-6 font-default text-2xl font-bold text-white md:text-3xl">
                    Total Device Protection
                  </h3>

                  <p className="mt-3 text-sm text-white/70 md:text-base leading-relaxed">
                    Protect every screen in your household from web-borne viruses, unsafe download
                    links, and predatory scams through an intuitive interface.
                  </p>

                  <div className="my-8 h-px w-full bg-[hsl(221,30%,15%)]" />

                  {/* Feature Checklist */}
                  <ul className="space-y-4">
                    {features.map((feature, i) => <li key={feature.id} data-index={i} className="flex items-center gap-3 text-sm text-white/90 md:text-base">
                        <CheckCircle2 className="size-5 shrink-0 text-primary" />
                        <span>{feature.text}</span>
                      </li>)}
                  </ul>

                  {/* CTA Button */}
                  <div className="mt-10">
                    <Button asChild className="group h-14 w-full bg-primary text-primary-foreground font-semibold shadow-lg hover:bg-primary/90 transition-[background-color,transform] active:scale-[0.99]">
                      <Link to="https://vpngenix.com/go/F-Secure" newTab className="flex items-center justify-center gap-2 text-base">
                        <span>Visit Site</span>
                        <ArrowRight className="size-5 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}